import { SuccessResponseModel } from '@models';
import { AccountApprovalCreateModel, AccountUpdatePatchModel } from '@models/accounts';
import * as Sentry from '@sentry/nextjs';
import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../store';
import { useImprvApi } from './useImprvApi';
import { useInMemoryStorage } from './useInMemoryStorage';

export const useAccountsService = () => {
  const [isPendingMarkIntroAsDone, setIsPendingMarkIntroAsDoneInner] = useState(false);
  const [introIsDone, setIntroIsDone] = useState(false);

  const [isPendingGetAccountMe, setIsPendingGetAccountMeInner] = useState(false);
  const [errorGetAccountMe, setErrorGetAccountMeInner] = useState(null);
  const [isPendingCreateAccountApprovals, setIsPendingCreateAccountApprovalsInner] =
    useState(false);

  // PREFERENCES
  const [videoPlaybackSpeed, setVideoPlaybackSpeedInner] = useState(1);

  const imprvApi = useImprvApi();
  const {
    userId: userId,
    setUserId: setUserId,
    accountMe: accountMe,
    setAccountMe: setAccountMe
  } = useInMemoryStorage((state) => ({
    userId: state.userId,
    setUserId: state.setUserId,
    accountMe: state.accountMe,
    setAccountMe: state.setAccountMe
  }));
  // Use store for localStorage state management (init-once pattern)
  const {
    initIntroIsDone,
    introIsDone: introIsDoneFromStore,
    setIntroIsDone: setIntroIsDoneToStore,
    initVideoPlaybackSpeed,
    videoPlaybackSpeed: videoPlaybackSpeedFromStore,
    setVideoPlaybackSpeed: setVideoPlaybackSpeedToStore
  } = useStore((state) => ({
    initIntroIsDone: state.initIntroIsDone,
    introIsDone: state.introIsDone,
    setIntroIsDone: state.setIntroIsDone,
    initVideoPlaybackSpeed: state.initVideoPlaybackSpeed,
    videoPlaybackSpeed: state.videoPlaybackSpeed,
    setVideoPlaybackSpeed: state.setVideoPlaybackSpeed
  }));

  // Initialize localStorage values only once when userId changes
  useEffect(() => {
    if (_.isNil(userId)) {
      return;
    }

    // Initialize localStorage values into store state
    const initialIntroIsDone = initIntroIsDone();
    const initialVideoPlaybackSpeed = initVideoPlaybackSpeed();

    // Synchronize local component state with store state
    setIntroIsDone(initialIntroIsDone);
    setVideoPlaybackSpeedInner(initialVideoPlaybackSpeed);
  }, [userId, initIntroIsDone, initVideoPlaybackSpeed]);

  // Keep local component state in sync with store state
  useEffect(() => {
    setIntroIsDone(introIsDoneFromStore);
  }, [introIsDoneFromStore]);

  useEffect(() => {
    setVideoPlaybackSpeedInner(videoPlaybackSpeedFromStore);
  }, [videoPlaybackSpeedFromStore]);

  /**
   * Marks the intro as done for the current user
   * @returns Promise resolving to the SuccessResponseModel
   */
  const markIntroAsDone = useCallback(async (): Promise<SuccessResponseModel> => {
    try {
      setIsPendingMarkIntroAsDoneInner(true);
      const result = await imprvApi.markIntroAsDone();
      setIntroIsDone(true);
      const accountMeData = await imprvApi.getAccountMe();
      setAccountMe(accountMeData);
      setIntroIsDoneToStore(true);
      return result;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setIsPendingMarkIntroAsDoneInner(false);
    }
  }, [imprvApi, setAccountMe, setIntroIsDoneToStore]);

  /**
   * Get the current user's account
   * @param force - Force refetch even if accountMe is already set
   * @returns Promise resolving to the AccountModel
   */
  const getAccountMe = useCallback(
    async (force: boolean = false) => {
      setIsPendingGetAccountMeInner(true);
      if (accountMe && !force) {
        // Set user ID in Sentry when we already have the account info
        Sentry.setUser({ id: accountMe?.user?.id?.toString() });
        setIsPendingGetAccountMeInner(false);
        return accountMe;
      }
      try {
        const accountMeData = await imprvApi.getAccountMe();
        // Set user ID in Sentry after successful fetch
        Sentry.setUser({ id: accountMeData?.user?.id?.toString() });

        // in-memory storage
        setAccountMe(accountMeData);
        setUserId(accountMeData.user.id);

        /**
         * Synchronize the API with the local storage
         */
        // if there's a mismatch between the API and the local storage we should sync the
        // API with the local storage
        if (!accountMeData.introIsDone && introIsDoneFromStore) {
          markIntroAsDone().then(() => {
            setAccountMe({ ...accountMeData, introIsDone: true });
          });
        }

        // if the API says that the intro is done and the local storage says that it's not done
        // we should mark the intro as done
        if (accountMeData.introIsDone && !introIsDoneFromStore) {
          setIntroIsDoneToStore(true);
        }

        return accountMeData;
      } catch (error) {
        setErrorGetAccountMeInner(error);
        Sentry.captureException(error);
        // if the error is not a 401, we should throw the error, otherwise the logout mechanism
        // of the API will be triggered
        if (error.status !== 401) {
          throw error;
        }
      } finally {
        setIsPendingGetAccountMeInner(false);
      }
    },
    [
      imprvApi,
      accountMe,
      introIsDoneFromStore,
      setAccountMe,
      setUserId,
      setIntroIsDoneToStore,
      markIntroAsDone
    ]
  );

  /**
   * Update the current user's account
   * @param accountUpdatePatch - The account update patch to apply
   * @returns Promise resolving to the SuccessResponseModel
   */
  const updateAccountMe = useCallback(
    async (accountUpdatePatch: AccountUpdatePatchModel) => {
      try {
        const result = await imprvApi.updateAccountMe(accountUpdatePatch);
        return result;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getIntroIsDone = useCallback((): boolean => {
    if (_.isNil(userId)) {
      return false;
    }
    return introIsDone || introIsDoneFromStore;
  }, [userId, introIsDone, introIsDoneFromStore]);

  /**
   * Set the video playback speed
   * @param speed - The speed to set the video playback to
   */
  const setVideoPlaybackSpeed = useCallback(
    (speed: number) => {
      setVideoPlaybackSpeedToStore(speed);
      setVideoPlaybackSpeedInner(speed);
    },
    [setVideoPlaybackSpeedToStore]
  );

  /**
   * Creates account approvals for the current user
   * @param approvals - The approvals to create
   * @returns Promise resolving to the SuccessResponseModel
   */
  const createAccountApprovals = useCallback(
    async (approvals: AccountApprovalCreateModel[]): Promise<SuccessResponseModel> => {
      try {
        setIsPendingCreateAccountApprovalsInner(true);
        const result = await imprvApi.createAccountApprovals(approvals);
        setIsPendingCreateAccountApprovalsInner(false);
        return result;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingCreateAccountApprovalsInner(false);
      }
    },
    [imprvApi]
  );

  return {
    // INTRO IS DONE
    markIntroAsDone,
    introIsDone,
    getIntroIsDone,
    isPendingMarkIntroAsDone,

    // USER
    userId,

    // ACCOUNT ME
    getAccountMe,
    accountMe,
    isPendingGetAccountMe,
    errorGetAccountMe,
    updateAccountMe,

    // PREFERENCES - VIDEO PLAYBACK SPEED
    videoPlaybackSpeed,
    setVideoPlaybackSpeed,

    // ACCOUNT APPROVALS
    createAccountApprovals,
    isPendingCreateAccountApprovals
  };
};
